import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Wand2, Gamepad2, Rocket, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const CREATOR_ROLES = ["creator", "educator", "admin"];

const STARTERS = [
  { label: "🚀 Neon space shooter", idea: "A fast neon space shooter where you dodge asteroids and blast alien ships", style: "Cyberpunk" },
  { label: "🌴 Jungle platformer", idea: "A jungle platformer starring a brave monkey collecting bananas across vines", style: "Cartoon" },
  { label: "🍕 Pizza dash", idea: "A cooking dash game where you build and deliver pizzas before the timer runs out", style: "Pixel art" },
  { label: "🏰 Castle defense", idea: "A castle defense game where you protect the gate from waves of goblins", style: "Fantasy" },
];

// Guided prompt→publish tour: 4 quick steps explaining the journey, then a starter picker
// so brand-new creators hit their first playable game fast. Shows once per browser.
const TOUR = [
  { icon: Mic, tint: "from-[#3b9eff] to-[#1f6feb]", title: "1. Imagine it", body: "Speak or type any idea — “A cat skateboards through space collecting fish.” No coding, ever." },
  { icon: Wand2, tint: "from-[#7c3aed] to-[#a855f7]", title: "2. Build it", body: "Studio designs the world, characters, rules and levels and hands you a real, playable game in seconds." },
  { icon: Gamepad2, tint: "from-[#22c55e] to-[#14b8a6]", title: "3. Play & grow it", body: "Play instantly, then keep prompting: add levels, new characters, a boss battle or multiplayer. Your game grows with your imagination." },
  { icon: Rocket, tint: "from-[#f59e0b] to-[#ec4899]", title: "4. Publish & earn", body: "Publish to the GameView Arcade, build a community and monetise your creation. Kid games get parent approval first." },
];

export default function FirstRunWelcome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user || !CREATOR_ROLES.includes(user.role)) return;
    if (localStorage.getItem("gv_firstrun_seen")) return;
    api.get("/growth/onboarding").then((r) => {
      if ((r.data?.done ?? 1) === 0) setOpen(true);
    }).catch(() => {});
  }, [user]);

  const seen = () => { localStorage.setItem("gv_firstrun_seen", "1"); setOpen(false); };
  const go = (params) => { seen(); navigate(`/app/create${params}`); };
  const isLast = step === TOUR.length; // final step = starter picker
  const cur = TOUR[step];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) seen(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="firstrun-welcome">
        <DialogTitle className="sr-only">Welcome to GameView Studio — quick tour</DialogTitle>
        <DialogDescription className="sr-only">A short guided tour showing how to go from an idea to a published game.</DialogDescription>
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 pt-5" data-testid="tour-progress">
          {[...TOUR, "start"].map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[hsl(var(--primary))]" : i < step ? "w-1.5 bg-[hsl(var(--primary))]/50" : "w-1.5 bg-[hsl(var(--muted-foreground))]/30"}`} />
          ))}
        </div>

        {!isLast ? (
          <div className="px-6 pb-6 pt-4 text-center" data-testid={`tour-step-${step}`}>
            <div className={`w-16 h-16 rounded-2xl grid place-items-center bg-gradient-to-br ${cur.tint} mx-auto mb-4 shadow-lg`}>
              <cur.icon className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-display text-2xl font-extrabold mb-2">{cur.title}</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-sm mx-auto">{cur.body}</p>
            <div className="flex items-center justify-between mt-6">
              {step > 0
                ? <Button variant="ghost" onClick={() => setStep((s) => s - 1)} className="rounded-full" data-testid="tour-back"><ArrowLeft className="w-4 h-4" /> Back</Button>
                : <button onClick={seen} className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="tour-skip">Skip tour</button>}
              <Button onClick={() => setStep((s) => s + 1)} className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]" data-testid="tour-next">
                Next <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="px-6 pb-6 pt-4 text-center" data-testid="tour-start">
            <h2 className="font-display text-2xl font-extrabold mb-1">Let's make your first game 🎮</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">Pick a starter idea or let Studio surprise you — you'll be playing in about 60 seconds.</p>
            <div className="grid grid-cols-2 gap-2">
              {STARTERS.map((s) => (
                <button key={s.label} onClick={() => go(`?idea=${encodeURIComponent(s.idea)}&style=${encodeURIComponent(s.style)}`)}
                  data-testid={`firstrun-starter-${s.style.replace(/\s+/g, "-").toLowerCase()}`}
                  className="rounded-xl border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 p-3 text-left text-sm font-semibold transition-colors">
                  {s.label}
                </button>
              ))}
            </div>
            <Button onClick={() => go("?surprise=1")} size="lg" className="rounded-full mt-3 w-full bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]" data-testid="firstrun-surprise">
              <Wand2 className="w-4 h-4" /> Surprise me — build instantly
            </Button>
            <div className="flex items-center justify-between mt-2">
              <Button variant="ghost" onClick={() => setStep((s) => s - 1)} className="rounded-full text-xs h-8" data-testid="tour-back"><ArrowLeft className="w-3.5 h-3.5" /> Back</Button>
              <button onClick={seen} className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="firstrun-skip">I'll explore first</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
